import React, { useState, useEffect, useMemo } from "react";
import Select, { ActionMeta, SingleValue } from "react-select";
import useErrorHandler from "@beeldit/core/errors/useErrorHandler";

import index from "@beeldit/core/services";
import { TreatmentSessionType } from "../models/TreatmentSessionType";

interface Option {
  label: string;
  value: number | string;
}

function CustomSelectWidget(props: any) {
  const { id, value, required, disabled, readonly, onChange, formContext } = props;
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [mapById, setMapById] = useState<Record<string, any>>({});
  const errorHandler = useErrorHandler();

  const { element } = formContext;
  const treatmentId = element?.treatment_id ?? null;

  // Deshabilitado si:
  // - viene disabled por props
  // - readonly (función o booleano)
  // - no hay treatment_id
  const disabledByreadOnly = () => {
    if (typeof readonly === "function") {
      return readonly(element);
    }
    return !!readonly;
  };

  const isDisabledComputed = useMemo(
    () => disabled || disabledByreadOnly() || !treatmentId,
    [disabled, readonly, element, treatmentId]
  );

  // Cargar opciones cuando cambie el treatment_id
  useEffect(() => {
    // Si no hay tratamiento, vaciamos opciones y selección
    if (!treatmentId) {
      setOptions([]);
      setSelectedOption(null);
      return;
    }

    const query = `treatment_id=${encodeURIComponent(treatmentId)}`;

    index("treatment-session-types", query)
      .then((response: any) => {
        const elements: TreatmentSessionType[] = response.data?.elements ?? [];
        const mapped: Option[] = elements.map((t) => ({
          label: `${t.name} - ${t.duration} meses`,
          value: t.id,
        }));
        setOptions(mapped);
        // guardar mapping id -> objeto para acceder al price al seleccionar
        const m: Record<string, any> = {};
        elements.forEach((e) => {
          m[String(e.id)] = e;
        });
        setMapById(m);

        // Mantener sincronizada la opción seleccionada si el value coincide
        const current = mapped.find((opt) => opt.value === value) || null;
        setSelectedOption(current);
      })
      .catch((error: any) => {
        errorHandler(error, null);
        setOptions([]);
        setSelectedOption(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentId, value]);

  // Si el value cambia externamente y hay opciones cargadas, sincronizamos
  useEffect(() => {
    if (!options.length) {
      setSelectedOption(null);
      return;
    }
    const currentSelectedOption = options.find((option) => option.value === value) || null;
    setSelectedOption(currentSelectedOption);
  }, [options, value]);

  const handleOnChange = (
    newValue: SingleValue<Option>,
    _actionMeta: ActionMeta<Option>
  ) => {
    const selected = (newValue as Option) || null;
    setSelectedOption(selected);
    onChange(selected?.value ?? null);
    try {
      const fc: any = formContext;
      const selectedId = selected?.value;
      if (selectedId && mapById[String(selectedId)]) {
        const sessionType = mapById[String(selectedId)];
            // Ensure setElement includes the selected treatment_session_type_id
            if (fc && typeof fc.setElement === 'function') {
              const current = fc.element || {};
              const updated = {
                ...current,
                base_price: sessionType.price,
                treatment_session_type_id: selectedId,
              };
              fc.setElement(updated);
        }
      }
    } catch (e) {}
  };

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: "#E7F5F2",
      backgroundColor: "#E7F5F2",
      borderRadius: "0.25rem",
    }),
  };

  return (
    <Select
      id={id}
      options={options}
      value={selectedOption}
      required={required}
      isDisabled={isDisabledComputed}
      onChange={handleOnChange}
      styles={selectStyles}
      placeholder={
        !treatmentId
          ? "Selecciona primero un tratamiento"
          : "Selecciona un tipo de sesión de tratamiento"
      }
      isClearable={true}
    />
  );
}

function TreatmentSessionTypeSelector(props: any) {
  const { id, options, value, required, disabled, readonly, onChange, formContext } = props;
  return (
    <CustomSelectWidget
      id={id}
      options={options?.enumOptions}
      value={value}
      required={required}
      disabled={disabled}
      readonly={readonly}
      onChange={onChange}
      formContext={formContext}
    />
  );
}

export default TreatmentSessionTypeSelector;
